import { computed, ref } from "vue";
import { useStore } from 'vuex';

const PORTAL_FIELDS = Object.freeze({
    1: {
        slug: 'fincaraiz',
        fields: [
            { key: 'user', label: 'Usuario', type: 'text', required: true },
            { key: 'email', label: 'Email', type: 'email', required: true },
            { key: 'token', label: 'Token', type: 'password', required: true },
        ]
    },
    2: {
        slug: 'clasificados_el_pais',
        fields: [
            { key: 'username', label: 'Usuario', type: 'text', required: true },
            { key: 'password', label: 'Contraseña', type: 'password', required: true },
            { key: 'authorization', label: 'Autorización', type: 'text', required: false },
        ]
    },
    4: {
        slug: 'metro_cuadrado',
        fields: [
            { key: 'username', label: 'Usuario', type: 'text', required: true },
            { key: 'password', label: 'Contraseña', type: 'password', required: true },
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'xapikey', label: 'X-API-Key', type: 'password', required: true },
        ]
    },
    5: {
        slug: 'ciencuadras',
        fields: [
            { key: 'username', label: 'Usuario', type: 'text', required: true },
            { key: 'password', label: 'Contraseña', type: 'password', required: true },
        ]
    },
    9: {
        slug: 'mercado_libre',
        fields: [
            { key: 'email', label: 'Email', type: 'email', required: true },
            { key: 'password', label: 'Contraseña', type: 'password', required: true },
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
            { key: 'redirect_uri', label: 'Redirect URI', type: 'text', required: true },
            { key: 'grant_type', label: 'Grant Type', type: 'text', required: false },
            { key: 'code', label: 'Code', type: 'text', required: false },
        ]
    },
});

const PROPPIT_FIELDS = Object.freeze([
    { key: 'username', label: 'Usuario', type: 'text', required: true },
    { key: 'password', label: 'Contraseña', type: 'password', required: true },
]);

export function useCredencialesPortales() {
    const store = useStore();

    const spinner = ref(true);
    const isLoading = ref(false);
    const alertTable = ref({ state: false, message: "", color: "success" });

    const ALERT_TIMEOUT = 5000;
    const SUCCESS_ALERT_TIMEOUT = 2000;

    // Filters
    const filterEmail = ref('');
    const filterCode = ref('');

    // Modal state
    const showCredentialForm = ref(false);
    const showAssignModal = ref(false);
    const showCellActions = ref(false);

    // Selected context
    const selectedPortal = ref(null);
    const selectedUser = ref(null);
    const selectedCell = ref(null);
    const existingCredentialData = ref(null);

    const showAlert = (message, timeout = ALERT_TIMEOUT, color = "success") => {
        alertTable.value.state = true;
        alertTable.value.message = message;
        alertTable.value.color = color;

        setTimeout(() => {
            alertTable.value.state = false;
            alertTable.value.message = "";
        }, timeout);
    };

    const handleError = (error, defaultMessage = "Ha ocurrido un error") => {
        console.error('Error:', error);
        const message = error?.response?.data?.message || error?.message || defaultMessage;
        showAlert(message, ALERT_TIMEOUT, "danger");
    };

    const dispatchWithErrorHandling = async (action, payload = null) => {
        try {
            return await store.dispatch(action, payload);
        } catch (error) {
            handleError(error);
            throw error;
        }
    };

    // Get field config for a portal
    const getPortalFields = (portalId, portalSlug) => {
        if (PORTAL_FIELDS[portalId]) {
            return PORTAL_FIELDS[portalId].fields;
        }
        if (portalSlug === 'proppit') {
            return PROPPIT_FIELDS;
        }
        // Fallback: check by slug in known configs
        for (const config of Object.values(PORTAL_FIELDS)) {
            if (config.slug === portalSlug) {
                return config.fields;
            }
        }
        return PROPPIT_FIELDS;
    };

    // Get credential info for a user/portal pair from the users data
    const getUserPortalInfo = (userItem, portalId) => {
        if (!userItem?.portales) return null;
        return userItem.portales.find(p => p.portal_id === portalId) || null;
    };

    // Determine credential type label
    const getCredentialType = (portalInfo) => {
        if (!portalInfo) return null;
        if (portalInfo.has_own_credential) return 'propia';
        if (portalInfo.shared_count > 0) return 'compartida';
        return null;
    };

    // Data fetching
    const fetchUsers = async () => {
        spinner.value = true;
        try {
            const params = {};
            if (filterEmail.value) params.email = filterEmail.value;
            if (filterCode.value) params.code = filterCode.value;
            await dispatchWithErrorHandling('appCredentialPortal/fetchUsers', params);
        } finally {
            spinner.value = false;
        }
    };

    const fetchPortalCredentials = async (portalId) => {
        try {
            return await dispatchWithErrorHandling('appCredentialPortal/fetchPortalCredentials', portalId);
        } catch (error) {
            return null;
        }
    };

    // Cell click handler
    const onCellClick = (userItem, portal) => {
        const portalInfo = getUserPortalInfo(userItem, portal.id);
        const credType = getCredentialType(portalInfo);

        selectedUser.value = {
            id: userItem.user.id,
            name: `${userItem.user.name} ${userItem.user.last_name || ''}`.trim(),
            email: userItem.user.email,
            is_admin: userItem.is_admin || false,
        };
        selectedPortal.value = portal;
        selectedCell.value = {
            user: selectedUser.value,
            portal,
            portalInfo,
            type: credType,
            status: portalInfo?.status || null,
            credential_id: portalInfo?.credential_id || null,
        };
        showCellActions.value = true;
    };

    // Open credential form (create or edit)
    const openCredentialForm = async (user, portal, isEdit = false) => {
        selectedUser.value = user;
        selectedPortal.value = portal;
        existingCredentialData.value = null;
        showCellActions.value = false;

        if (isEdit && portal) {
            try {
                const data = await fetchPortalCredentials(portal.id);
                if (data?.credentials) {
                    const cred = data.credentials.find(
                        c => c.user?.id == user.id || c.user_id == user.id
                    );
                    if (cred) {
                        existingCredentialData.value = cred.credential || cred;
                    }
                }
            } catch (e) {
                console.warn('Error al cargar credencial para editar:', e);
            }
        }

        showCredentialForm.value = true;
    };

    // Open batch assign modal
    const openAssignModal = async (portal = null) => {
        selectedPortal.value = portal;
        showAssignModal.value = true;
        await fetchMembershipUsers();
    };

    // Helper para limpiar estado de modales
    const resetFormState = () => {
        showCredentialForm.value = false;
        selectedUser.value = null;
        selectedPortal.value = null;
        existingCredentialData.value = null;
    };

    const resetCellState = () => {
        showCellActions.value = false;
        selectedCell.value = null;
    };

    // Save credential
    const saveCredential = async (formData) => {
        const portalId = selectedPortal.value.id;
        const userId = selectedUser.value.id;
        resetFormState();
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling('appCredentialPortal/saveCredential', {
                portalId,
                userId,
                formData
            });
            showAlert("Credencial guardada correctamente", SUCCESS_ALERT_TIMEOUT);
            await fetchUsers();
        } finally {
            isLoading.value = false;
        }
    };

    // Delete credential
    const deleteCredential = async () => {
        const cell = selectedCell.value;
        if (!cell) return;
        resetCellState();
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling('appCredentialPortal/deleteCredential', {
                portalId: cell.portal.id,
                userId: cell.user.id
            });
            showAlert("Credencial eliminada", SUCCESS_ALERT_TIMEOUT);
            await fetchUsers();
        } finally {
            isLoading.value = false;
        }
    };

    // Revoke shared credential (unassign single user)
    const revokeShared = async () => {
        const cell = selectedCell.value;
        if (!cell) return;
        resetCellState();
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling('appCredentialPortal/unassignCredentials', {
                portalId: cell.portal.id,
                userIds: [cell.user.id]
            });
            showAlert("Credencial revocada", SUCCESS_ALERT_TIMEOUT);
            await fetchUsers();
        } finally {
            isLoading.value = false;
        }
    };

    // Cargar credenciales disponibles de un portal (endpoint 2)
    const portalCredentialsList = ref([]);
    const loadingPortalCredentials = ref(false);

    const fetchPortalCredentialsList = async (portalId) => {
        loadingPortalCredentials.value = true;
        try {
            const data = await dispatchWithErrorHandling('appCredentialPortal/fetchPortalCredentials', portalId);
            portalCredentialsList.value = data?.credentials || [];
        } catch (e) {
            portalCredentialsList.value = [];
        } finally {
            loadingPortalCredentials.value = false;
        }
    };

    // Batch assign — legacy usa credentialId, OAuth usa oauthOwnerUserId
    const batchAssign = async (portalId, credentialId, userIds, oauthOwnerUserId, isOAuth) => {
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling('appCredentialPortal/assignCredentials', {
                portalId,
                credentialId,
                userIds,
                oauthOwnerUserId,
                isOAuth,
            });
            showAlert("Credenciales compartidas correctamente", SUCCESS_ALERT_TIMEOUT);
            showAssignModal.value = false;
            await fetchUsers();
        } finally {
            isLoading.value = false;
        }
    };

    // Batch unassign
    const batchUnassign = async (portalId, userIds) => {
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling('appCredentialPortal/unassignCredentials', {
                portalId,
                userIds
            });
            showAlert("Credenciales revocadas correctamente", SUCCESS_ALERT_TIMEOUT);
            showAssignModal.value = false;
            await fetchUsers();
        } finally {
            isLoading.value = false;
        }
    };

    // Delete specific share
    const deleteShare = async (shareId, portalId) => {
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling('appCredentialPortal/deleteShare', shareId);
            showAlert("Asignación eliminada correctamente", SUCCESS_ALERT_TIMEOUT);
            // Refresh credentials list for the portal
            if (portalId) {
                await fetchPortalCredentialsList(portalId);
            }
            await fetchUsers();
        } finally {
            isLoading.value = false;
        }
    };

    // Toggle sync status
    const toggleStatus = async (portalId, userId) => {
        try {
            await dispatchWithErrorHandling('appCredentialPortal/toggleSyncStatus', {
                portalId,
                userId
            });
            showAlert("Estado de sincronización actualizado", SUCCESS_ALERT_TIMEOUT);
            showCellActions.value = false;
            selectedCell.value = null;
            await fetchUsers();
        } catch (e) {
            // handled by dispatchWithErrorHandling
        }
    };

    // Close handlers
    const closeCredentialForm = () => {
        showCredentialForm.value = false;
        selectedUser.value = null;
        selectedPortal.value = null;
        existingCredentialData.value = null;
    };

    const closeAssignModal = () => {
        showAssignModal.value = false;
        selectedPortal.value = null;
    };

    const closeCellActions = () => {
        showCellActions.value = false;
        selectedCell.value = null;
    };

    // Cargar lista de usuarios desde membresías (para el modal de compartir)
    const membershipUsers = ref([]);
    const membershipPagination = ref({ current_page: 1, last_page: 1, total: 0 });
    const loadingMembers = ref(false);

    const fetchMembershipUsers = async (filters = {}, page = 1) => {
        loadingMembers.value = true;
        try {
            await store.dispatch('appMembership/storeMemberships', { filters, page });
            const memberships = store.state.appMembership?.memberships || {};
            membershipUsers.value = memberships.data || [];
            membershipPagination.value = {
                current_page: memberships.current_page || 1,
                last_page: memberships.last_page || 1,
                total: memberships.total || 0,
            };
        } catch (e) {
            console.error('Error cargando usuarios de membresías:', e);
        } finally {
            loadingMembers.value = false;
        }
    };

    // Computed
    const usersData = computed(() => store.state.appCredentialPortal?.users || []);
    const portales = computed(() => store.state.appCredentialPortal?.portales || []);
    const adminUser = computed(() => usersData.value.find(u => u.is_admin) || null);
    const subUsers = computed(() => usersData.value.filter(u => !u.is_admin));

    // Check if admin has own credential for a given portal
    const adminHasCredentialForPortal = (portalId) => {
        if (!adminUser.value) return false;
        const portalInfo = getUserPortalInfo(adminUser.value, portalId);
        return portalInfo?.has_own_credential || false;
    };

    return {
        // Filters
        filterEmail,
        filterCode,

        // State
        spinner,
        isLoading,
        alertTable,
        showCredentialForm,
        showAssignModal,
        showCellActions,
        selectedPortal,
        selectedUser,
        selectedCell,
        existingCredentialData,

        // Data
        usersData,
        portales,
        adminUser,
        subUsers,
        membershipUsers,
        membershipPagination,
        loadingMembers,
        fetchMembershipUsers,

        // Portal credentials (endpoint 2)
        portalCredentialsList,
        loadingPortalCredentials,
        fetchPortalCredentialsList,

        // Config
        getPortalFields,

        // Helpers
        getUserPortalInfo,
        getCredentialType,
        adminHasCredentialForPortal,

        // Actions
        fetchUsers,
        fetchPortalCredentials,
        onCellClick,
        openCredentialForm,
        openAssignModal,
        saveCredential,
        deleteCredential,
        revokeShared,
        batchAssign,
        batchUnassign,
        deleteShare,
        toggleStatus,

        // Close handlers
        closeCredentialForm,
        closeAssignModal,
        closeCellActions,
    };
}
